// [S-788a / E-HARNESS] /platform/evals — the eval scorecard, readable before
// anyone tests by hand. Same pattern as /atlas: a real admin route serves the
// latest published scorecard; this screen renders it and nothing else.
//
// It answers, in order: did anything regress → what is green now → are the
// ceilings holding → what is in shadow. Regressed flows are pinned first.
// The re-run for a flow is shown as copyable text — never a button that
// pretends to run it (no-fake-UI).
import { useMemo, useState } from "react";
import { Alert, LoadingState, PageHead, Pill, SectionHead, Select } from "../components";
import { useEvalScorecard, type ScorecardFlow } from "../data/evalsQuery";

type StatusFilter = "all" | "problems" | "pass" | "skipped";

function outcomePill(f: ScorecardFlow): JSX.Element {
  if (f.regressed) return <Pill variant="high">regressed</Pill>;
  if (f.replay === "fail") return <Pill variant="high">fail</Pill>;
  if (f.replay === "stale") return <Pill variant="medium">stale</Pill>;
  if (f.replay === "skipped") return <Pill variant="low">skipped</Pill>;
  return <Pill variant="low">pass</Pill>;
}

export function Evals(): JSX.Element {
  const q = useEvalScorecard();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [tag, setTag] = useState<string>("all");
  const [open, setOpen] = useState<string | null>(null);

  const card = q.data !== undefined && !q.data.notPublished ? q.data.card : null;
  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const f of card?.flows ?? []) for (const t of f.tags) set.add(t);
    return [...set].sort();
  }, [card]);
  const rows = useMemo(() => {
    if (card === null) return [];
    return card.flows.filter((f) => {
      if (tag !== "all" && !f.tags.includes(tag)) return false;
      if (status === "problems") return f.regressed || f.replay === "fail" || f.replay === "stale";
      if (status === "pass") return f.replay === "pass";
      if (status === "skipped") return f.replay === "skipped";
      return true;
    });
  }, [card, status, tag]);

  if (q.isPending) return <LoadingState label="Loading the scorecard…" />;
  if (q.isError)
    return (
      <Alert label="Couldn't load the scorecard" tone="error">
        {q.error.message}
      </Alert>
    );
  if (q.data.notPublished) {
    return (
      <>
        <PageHead title="Evals" />
        <Alert label="No scorecard published yet">
          {q.data.message} Publish one with <code>pnpm evals:replay</code> (writes <code>docs/evals/</code>) — the nightly publishes automatically once S-788 lands.
        </Alert>
      </>
    );
  }
  const c = q.data.card;
  const s = c.summary;
  const regressed = c.flows.filter((f) => f.regressed);
  const failing = c.flows.filter((f) => !f.regressed && (f.replay === "fail" || f.replay === "stale"));
  const badBudgets = c.budgets.filter((b) => !b.ok);

  return (
    <>
      <PageHead title="Evals" />
      <p>
        <strong>{c.run.kind}</strong> @ <code>{c.run.commit}</code> · {new Date(c.run.at).toLocaleString()} ·{" "}
        {c.baseline === null ? "this run is the baseline" : <>baseline <code>{c.baseline.commit}</code></>}
      </p>
      <p>
        <Pill variant={s.regressions > 0 ? "high" : "low"}>{s.regressions} regressed</Pill>{" "}
        <Pill variant={s.fail > 0 ? "high" : "low"}>{s.fail} fail</Pill>{" "}
        <Pill variant={s.stale > 0 ? "medium" : "low"}>{s.stale} stale</Pill>{" "}
        <Pill variant="low">{s.pass} pass</Pill> <Pill variant="low">{s.skipped} skipped</Pill>{" "}
        <Pill variant={badBudgets.length > 0 ? "high" : "low"}>{badBudgets.length === 0 ? "budgets ok" : `${badBudgets.length} budgets over`}</Pill>{" "}
        <span>{s.modelCalls} model calls</span>
      </p>

      <SectionHead title="Regressions and failures" />
      {regressed.length + failing.length === 0 ? (
        <p>None.</p>
      ) : (
        <ul>
          {[...regressed, ...failing].map((f) => (
            <li key={f.id}>
              {outcomePill(f)} <code>{f.id}</code>
              {f.lastFailure !== null ? (
                <>
                  {" "}— turn {f.lastFailure.turn}: {f.lastFailure.assertion}
                </>
              ) : null}{" "}
              <code>{f.rerun}</code>
            </li>
          ))}
        </ul>
      )}

      <SectionHead title="Budgets" />
      <table>
        <thead>
          <tr>
            <th>budget</th>
            <th>value</th>
            <th>ceiling</th>
            <th>ok</th>
          </tr>
        </thead>
        <tbody>
          {c.budgets.map((b) => (
            <tr key={b.name}>
              <td>{b.name}</td>
              <td>{b.value}</td>
              <td>{b.ceiling}</td>
              <td>{b.ok ? "✓" : <Pill variant="high">over</Pill>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionHead title="Classification by tier" />
      <ul>
        {Object.entries(c.classification).map(([tier, t]) => (
          <li key={tier}>
            {tier}: {t.pass}/{t.total}
          </li>
        ))}
      </ul>
      {(c.gates ?? []).length === 0 ? (
        <p>Gates: all hold (stay_put 100 %, other tiers at or above baseline).</p>
      ) : (
        <Alert label={`${c.gates.length} classification gate(s) failing`} tone="error">
          <ul>
            {c.gates.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Alert>
      )}

      <SectionHead title="All flows" />
      <p>
        <Select<StatusFilter>
          label="Status"
          value={status}
          onValueChange={(v) => setStatus(v ?? "all")}
          options={[
            { value: "all", label: "All statuses" },
            { value: "problems", label: "Regressed / failed / stale" },
            { value: "pass", label: "Pass" },
            { value: "skipped", label: "Skipped (live-only)" },
          ]}
        />{" "}
        <Select<string>
          label="Tag"
          value={tag}
          onValueChange={(v) => setTag(v ?? "all")}
          options={[{ value: "all", label: "All tags" }, ...tags.map((t) => ({ value: t, label: t }))]}
        />
      </p>
      <table>
        <thead>
          <tr>
            <th>status</th>
            <th>flow</th>
            <th>persona</th>
            <th>calls</th>
            <th>since</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id} onClick={() => setOpen(open === f.id ? null : f.id)}>
              <td>{outcomePill(f)}</td>
              <td>
                <code>{f.id}</code> — {f.title}
                {open === f.id ? (
                  <div>
                    {f.lastFailure !== null ? (
                      <p>
                        turn {f.lastFailure.turn}: {f.lastFailure.assertion}
                      </p>
                    ) : null}
                    <p>
                      re-run: <code>{f.rerun}</code>
                    </p>
                    <p>tags: {f.tags.join(", ")}</p>
                  </div>
                ) : null}
              </td>
              <td>{f.persona}</td>
              <td>{f.modelCalls}</td>
              <td>{f.since}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
